import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { history } from '../store';
import { showErrorNotification, showSuccessNotification } from '../actions/App';
import * as noticeTemplatesActions from '../actions/NoticeTemplates';
import * as noticeTemplatesApi from '../api/NoticeTemplates';
import * as noticeTemplateModal from '../actions/Modal';
import {
  ADD_NOTICE_TEMPLATE,
  DELETE_NOTICE_TEMPLATE,
  EDIT_NOTICE_TEMPLATE,
  GET_NOTICE_TEMPLATE_BY_ID,
  GET_NOTICE_TEMPLATES,
} from '../constants/ActionTypes';

const storedTemplatesSelector = state => state.noticeTemplates.templates;

function* filterTemplates(templates) {
  const { recentlyAdded } = yield select(storedTemplatesSelector);

  if (!recentlyAdded.length) {
    return templates;
  }

  return templates.filter(template => !recentlyAdded.find(added => added.id === template.id));
}

function* editTemplate({ payload }) {
  yield put(noticeTemplatesActions.showNoticeLoader());

  try {
    const { data } = yield call(noticeTemplatesApi.editTemplate, payload.id, payload.template);
    yield put(noticeTemplatesActions.editNoticeTemplateSuccess(data));

    const storedTemplates = yield select(storedTemplatesSelector);

    if (storedTemplates && storedTemplates.list && storedTemplates.list.length) {
      let shouldUpdate;
      const templatesToUpdate = storedTemplates.list.map(template => {
        if (template.id === data.id) {
          shouldUpdate = true;

          return data;
        }

        return template;
      });

      if (shouldUpdate) {
        yield put(
          noticeTemplatesActions.getNoticeTemplatesSuccess(
            templatesToUpdate,
            storedTemplates.pageCount,
          ),
        );
      }
    }
    yield put(noticeTemplateModal.noticeTemplateModalSubmitedSuccess());
  } catch (error) {
    yield put(noticeTemplateModal.noticeTemplateModalSubmitedFailure());
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(noticeTemplatesActions.hideNoticeLoader());
  }
}

function* getTemplateById({ payload }) {
  yield put(noticeTemplatesActions.showNoticeLoader());

  try {
    const { data } = yield call(noticeTemplatesApi.getTemplateById, payload.id);
    yield put(noticeTemplatesActions.getNoticeTemplateByIdSuccess(data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(noticeTemplatesActions.getNoticeTemplateByIdFailure());
  } finally {
    yield put(noticeTemplatesActions.hideNoticeLoader());
  }
}

function* getTemplates({ payload }) {
  yield put(noticeTemplatesActions.showNoticeLoader());

  try {
    const { data } = yield call(noticeTemplatesApi.getTemplates, payload.loadingMore);

    if (payload.loadingMore) {
      const filteredTemplates = yield call(filterTemplates, data.entities);
      yield put(
        noticeTemplatesActions.getMoreNoticeTemplatesSuccess(filteredTemplates, data.pageCount),
      );
    } else {
      const filteredTemplates = yield call(filterTemplates, data.entities);
      yield put(
        noticeTemplatesActions.getNoticeTemplatesSuccess(filteredTemplates, data.pageCount),
      );
    }
  } catch (error) {
    yield put(noticeTemplatesActions.getNoticeTemplatesFailure(error && error.message));
    yield put(showErrorNotification('errors.getTemplates'));
  } finally {
    yield put(noticeTemplatesActions.hideNoticeLoader());
  }
}

function* deleteTemplate({ payload }) {
  yield put(noticeTemplatesActions.showNoticeLoader());
  const { recentlyAdded } = yield select(storedTemplatesSelector);

  const newAddedTemplates = recentlyAdded.filter(added => added.id !== payload.id);

  try {
    yield call(noticeTemplatesApi.deleteTemplate, payload.id);
    yield put(showSuccessNotification('success.deleteTemplate'));

    if (payload.urlToRedirect) {
      yield call(history.push, payload.urlToRedirect);
    } else {
      yield call(getTemplates, { payload: { loadingMore: false } });
    }
    yield put(noticeTemplatesActions.deleteNoticeTemplateSuccess(newAddedTemplates));
  } catch (error) {
    yield put(showErrorNotification('errors.deleteTemplate'));
  } finally {
    yield put(noticeTemplatesActions.hideNoticeLoader());
  }
}

function* addTemplate({ payload }) {
  yield put(noticeTemplatesActions.showNoticeLoader());

  try {
    const { data } = yield call(noticeTemplatesApi.addTemplate, payload.template);
    const template = { ...data, new: true };
    yield put(noticeTemplatesActions.addNoticeTemplateSuccess(template));
    yield put(noticeTemplateModal.noticeTemplateModalSubmitedSuccess());
  } catch (error) {
    yield put(noticeTemplateModal.noticeTemplateModalSubmitedFailure());
    yield put(showErrorNotification('errors.addTemplate'));
    yield put(noticeTemplatesActions.hideNoticeLoader());
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(ADD_NOTICE_TEMPLATE, addTemplate),
    takeLatest(DELETE_NOTICE_TEMPLATE, deleteTemplate),
    takeLatest(EDIT_NOTICE_TEMPLATE, editTemplate),
    takeLatest(GET_NOTICE_TEMPLATE_BY_ID, getTemplateById),
    takeLatest(GET_NOTICE_TEMPLATES, getTemplates),
  ]);
}
